import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  Text,
  useToast,
} from "@chakra-ui/react";
import { createChannel } from "../../services/messagingService";

// Step-up confirm: type your own account email, matched client-side against
// currentUser.email. This is NOT a credential check (unlike a password) —
// it's the same "type X to confirm" friction other destructive actions use,
// chosen specifically because it works for every account regardless of how
// they signed in (email/password or Google OAuth — an OAuth-only account has
// no password at all, so a password check would permanently lock them out).
// The real authorization is server-side and doesn't depend on this step:
// backend/api/messaging.py::internal_channel_create re-checks the actor's
// role (doctor or superadmin) against public.users itself.
export default function CreateChannelModal({ isOpen, onClose, currentUser, onCreated }) {
  const [step, setStep] = useState("confirm"); // 'confirm' | 'details'
  const [emailConfirm, setEmailConfirm] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [channelType, setChannelType] = useState("public");
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const reset = () => {
    setStep("confirm");
    setEmailConfirm("");
    setName("");
    setDescription("");
    setChannelType("public");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleConfirmEmail = () => {
    if (!emailConfirm.trim()) return;
    if (emailConfirm.trim().toLowerCase() !== (currentUser.email || "").toLowerCase()) {
      toast({ title: "That doesn't match your account email", status: "error", duration: 3000 });
      return;
    }
    setStep("details");
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const { channel } = await createChannel(name.trim(), description.trim(), channelType, currentUser.id);
      toast({ title: `#${channel.name} created`, status: "success", duration: 2500 });
      onCreated(channel);
      handleClose();
    } catch (err) {
      toast({ title: "Couldn't create channel", description: err.message, status: "error", duration: 4000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{step === "confirm" ? "Confirm it's you" : "Create channel"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {step === "confirm" ? (
            <FormControl>
              <FormLabel>Channel management is a deliberate action — type your account email ({currentUser.email}) to confirm</FormLabel>
              <Input
                value={emailConfirm}
                onChange={(e) => setEmailConfirm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleConfirmEmail()}
                placeholder={currentUser.email}
                autoFocus
              />
            </FormControl>
          ) : (
            <>
              <FormControl mb={3}>
                <FormLabel>Channel name</FormLabel>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. front-desk" autoFocus />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Description (optional)</FormLabel>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Type</FormLabel>
                <Select value={channelType} onChange={(e) => setChannelType(e.target.value)}>
                  <option value="public">Public — anyone can join</option>
                  <option value="private">Private — invite only</option>
                </Select>
              </FormControl>
              {channelType === "private" && (
                <Text fontSize="xs" color="gray.500" mt={2}>
                  After creating it, use the "+" next to the channel name to invite people — private channels don't show up in Browse.
                </Text>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          {step === "confirm" ? (
            <Button colorScheme="blue" onClick={handleConfirmEmail}>
              Continue
            </Button>
          ) : (
            <Button colorScheme="blue" isLoading={submitting} onClick={handleCreate}>
              Create
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
